/**
 * One place to learn that the session is gone, and one to learn that access has ended.
 *
 * A 401 from our API is not an ordinary request failure: it means the token no longer
 * authenticates, whether it expired, its silent renewal failed, or was never valid. All
 * of those need the same answer, a re-login prompt, and showing the generic "something
 * went wrong" instead leaves someone retrying a request that cannot succeed.
 *
 * A 403 carrying the `access_ended` code is the opposite case and needs the opposite
 * answer. The token authenticated fine; the person is known and no longer admitted,
 * because they were deactivated or their access ran past its date. Offering sign-in again
 * would send them to the identity provider, succeed there, and land them back on the same
 * refusal with nothing on screen saying why. So this one gets a terminal screen instead.
 *
 * Both are registered by the auth provider and called from `api/http.ts`, so every call
 * in the app is covered by the one place that already sees every response.
 */

import { authDebug } from './debug';

type Handler = () => void;

/** The backend's `detail.code` for a known account that is no longer admitted. */
export const ACCESS_ENDED_CODE = 'access_ended';

let unauthorizedHandler: Handler | null = null;
let accessEndedHandler: Handler | null = null;

export function setUnauthorizedHandler(fn: Handler | null): void {
  unauthorizedHandler = fn;
}

/**
 * Renews the token and reports whether a retry is worth making. Registered by the gate.
 *
 * A 401 is usually not the end of the session. After an idle break the access token has
 * expired while the page's renewal timer has not yet run, because the browser pauses
 * timers in a hidden or sleeping tab. The service worker then waits five seconds for a
 * renewal that has not started and answers 401 on its own. Measured on both providers on
 * 2026-10-07: the renewal succeeded a moment later, and only a reload got the person out
 * of the sign-in screen. So a 401 asks for one renewal before it counts as the session
 * being lost.
 */
type Recoverer = () => Promise<boolean>;

let recoverer: Recoverer | null = null;
let recovery: Promise<boolean> | null = null;

export function setSessionRecoverer(fn: Recoverer | null): void {
  recoverer = fn;
}

/**
 * How long a renewal may take before it counts as failed.
 *
 * The request deadline in `api/http.ts` does not cover this wait, so without one a
 * renewal that never settles leaves the send hanging with no error, and every later
 * request joins the same pending renewal. That was a message sent after a long idle
 * showing in the conversation and getting nothing back (issue #486). The library's own
 * token request gives up after 10 seconds, so a healthy renewal is well inside this.
 */
export const RECOVERY_TIMEOUT_MS = 10_000;

/**
 * One renewal for every request that is refused at the same moment. Coming back to the
 * app fires several calls at once, and each would otherwise start its own token request.
 */
export function recoverSession(): Promise<boolean> {
  if (!recoverer) return Promise.resolve(false);
  if (!recovery) {
    const started = Date.now();
    authDebug('renewal started after a 401');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<boolean>((resolve) => {
      timer = setTimeout(() => {
        authDebug('renewal timed out', { afterMs: Date.now() - started });
        resolve(false);
      }, RECOVERY_TIMEOUT_MS);
    });
    const renewal = recoverer().catch((err) => {
      authDebug('renewal threw', { error: String(err) });
      return false;
    });
    recovery = Promise.race([renewal, deadline]).finally(() => {
      clearTimeout(timer);
      recovery = null;
    });
    void renewal.then((ok) => authDebug('renewal settled', { ok, afterMs: Date.now() - started }));
  }
  return recovery;
}

/**
 * Starts the one silent sign-in when a renewal could not save a refused request, and
 * reports whether it did. Registered by the gate.
 *
 * A renewal that failed or timed out does not mean the person has to sign in: the
 * identity provider's own cookie usually outlives our tokens, so a fresh authorization
 * request comes straight back. `false` means the attempt is spent, and the sign-in
 * screen is what is left.
 */
type SignInAgain = () => boolean;

let signInAgainHandler: SignInAgain | null = null;

export function setSignInAgain(fn: SignInAgain | null): void {
  signInAgainHandler = fn;
}

export function signInAgain(): boolean {
  return signInAgainHandler?.() ?? false;
}

export function notifyUnauthorized(): void {
  unauthorizedHandler?.();
}

export function setAccessEndedHandler(fn: Handler | null): void {
  accessEndedHandler = fn;
}

export function notifyAccessEnded(): void {
  accessEndedHandler?.();
}

/**
 * Whether a 403 body is the backend saying access has ended, as opposed to any other
 * forbidden answer (an admin-only route reached by somebody who is not one, say).
 */
export function isAccessEndedBody(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return false;
  return (detail as { code?: unknown }).code === ACCESS_ENDED_CODE;
}
